/**
 * Deterministic, DOM-free navigation state machine for the refinance wizard
 * foundation (WI-TV-W5-001).
 *
 * The machine owns the ordered list of steps, the current step index, and the
 * flat map of entered values. It exposes deterministic Previous / Continue /
 * Skip transitions:
 *
 * - `next()` validates the current step and only advances when it is valid;
 *   required steps therefore have no bypass.
 * - `skip()` advances without validating, but only for steps flagged optional;
 *   it never discards values the user already supplied.
 * - `back()` moves to the previous step and always retains entered values.
 *
 * Progress semantics count applicable input steps only (`total`), so a future
 * thank-you state is never counted as another numbered input step. The machine
 * performs no rendering and reads no globals, which keeps navigation logic
 * unit-testable in isolation from the DOM component that drives it.
 *
 * A step may declare itself conditionally applicable (WI-TV-W65-006): an
 * inapplicable step does not exist for navigation, progress, or submission, and
 * the values it owns are never carried while it is absent. A step that declares
 * neither hook is always applicable and owns nothing, so journeys with a fixed
 * cadence behave exactly as they did before conditional applicability existed.
 */

/** A single validation problem for a control on the current step. */
export interface WizardFieldError {
  /** DOM id / value key of the offending control, used to move focus. */
  readonly field: string;
  /** Accessible, human-readable message shown inline and in the summary. */
  readonly message: string;
}

/**
 * Read/write view of wizard values handed to a step's validate/render hooks.
 * Values are flat strings keyed by a stable value key so the machine stays
 * serialization-friendly and free of field-specific typing.
 */
export interface WizardValues {
  get(key: string): string;
  set(key: string, value: string): void;
  /** Immutable snapshot of all entered values. */
  snapshot(): Readonly<Record<string, string>>;
}

/**
 * Contract every wizard step implements. Property/finance field bodies are
 * intentionally typed placeholder contracts here (WI-TV-W5-001 foundation); the
 * concrete field rendering/validation lands in later work items. The navigation
 * primitives (required vs optional/Skip, retention) are fully defined now.
 */
export interface WizardStep {
  /** Stable step id (also used for the DOM step container). */
  readonly id: string;
  /** Short numbered-step title shown in the step header. */
  readonly title: string;
  /**
   * When true the step exposes an explicit Skip control and may be bypassed
   * without validation. Required steps omit Skip and cannot be bypassed.
   */
  readonly optional?: boolean;
  /**
   * Value keys this step is the only surface for. They are cleared while the
   * step is inapplicable, so an answer the borrower can no longer see or
   * correct is never retained, snapshot, validated, or submitted.
   */
  readonly valueKeys?: readonly string[];
  /**
   * Reports whether the step applies to the current answers. A step that omits
   * this hook always applies. Applicability is derived from values alone, so it
   * is deterministic and free of navigation history.
   */
  applies?(values: WizardValues): boolean;
  /**
   * Validates the current values for this step. Returns an empty array when the
   * step is satisfied. Optional steps may still validate supplied values (e.g.
   * format), but an untouched optional step returns no errors.
   */
  validate(values: WizardValues): WizardFieldError[];
  /**
   * Renders the step body into `container`. Foundation placeholder steps render
   * a typed placeholder panel; real controls arrive in later work items. The
   * component wires input events back into {@link WizardValues}.
   */
  render(container: HTMLElement, values: WizardValues): void;
  /**
   * Invoked when an optional step is skipped, before the wizard advances. A
   * step whose answers must not survive an explicit Skip clears them here, so
   * skipping asserts nothing rather than carrying an answer the borrower chose
   * to leave behind.
   */
  onSkip?(values: WizardValues): void;
}

/** Progress summary for the current position; counts input steps only. */
export interface WizardProgress {
  /** 1-based index of the current step. */
  readonly current: number;
  /** Total number of numbered input steps. */
  readonly total: number;
  /** `current / total` in the range (0, 1], useful for the progress bar. */
  readonly ratio: number;
}

/** Result of a forward navigation attempt. */
export interface WizardAdvanceResult {
  /** True when navigation succeeded (step advanced or wizard completed). */
  readonly ok: boolean;
  /** Validation errors that blocked the advance; empty when `ok` is true. */
  readonly errors: WizardFieldError[];
}

function createValues(initial: Record<string, string>): WizardValues {
  const store = new Map<string, string>(Object.entries(initial));
  return {
    get: (key) => store.get(key) ?? "",
    set: (key, value) => {
      store.set(key, value);
    },
    snapshot: () => Object.fromEntries(store.entries()),
  };
}

/**
 * Wraps a plain record as a {@link WizardValues} view, so an applicability hook
 * can be evaluated against hypothetical answers (for example the answers a
 * prefill would produce) without mutating a mounted wizard.
 */
export function valuesView(record: Readonly<Record<string, string>>): WizardValues {
  return createValues({ ...record });
}

/**
 * Value keys owned by the steps of `steps` that do not apply to `values`. A
 * mounted wizard clears exactly these keys, so a caller seeding a wizard can
 * ask the same question before the mount and report only what the wizard will
 * actually hold.
 */
export function inapplicableValueKeys(
  steps: readonly WizardStep[],
  values: Readonly<Record<string, string>>,
): string[] {
  const view = valuesView(values);
  return steps
    .filter((step) => step.applies?.(view) === false)
    .flatMap((step) => [...(step.valueKeys ?? [])]);
}

export class WizardMachine {
  private readonly steps: readonly WizardStep[];
  private readonly valueStore: WizardValues;
  /** Id of the current step; positions are always derived from it. */
  private currentId: string;
  private completed = false;

  constructor(steps: readonly WizardStep[], initialValues: Record<string, string> = {}) {
    if (steps.length === 0) throw new Error("WizardMachine requires at least one step.");
    this.steps = steps;
    this.valueStore = createValues(initialValues);
    // Seeded values belonging to a step the answers make inapplicable are
    // dropped before anything reads them, so a mount can never start holding an
    // answer its own cadence does not ask for.
    this.pruneInapplicableValues();
    const first = this.applicableSteps[0];
    if (!first) throw new Error("WizardMachine requires at least one applicable step.");
    this.currentId = first.id;
  }

  /**
   * The steps that apply to the current answers, in the declared order. A
   * cadence whose every step is conditional would leave nothing to render, so
   * the declared list stands in that degenerate case rather than producing a
   * wizard with no steps at all.
   */
  get applicableSteps(): readonly WizardStep[] {
    const applicable = this.steps.filter((step) => step.applies?.(this.valueStore) !== false);
    return applicable.length > 0 ? applicable : this.steps;
  }

  /** Value keys owned by steps that do not currently apply. */
  private inapplicableValueKeys(): string[] {
    const applicable = this.applicableSteps;
    return this.steps
      .filter((step) => !applicable.includes(step))
      .flatMap((step) => [...(step.valueKeys ?? [])]);
  }

  /**
   * Clears every value owned by a step that does not currently apply and
   * returns the keys that actually held something. Idempotent.
   */
  pruneInapplicableValues(): string[] {
    const cleared: string[] = [];
    for (const key of this.inapplicableValueKeys()) {
      if (this.valueStore.get(key) === "") continue;
      this.valueStore.set(key, "");
      cleared.push(key);
    }
    return cleared;
  }

  /**
   * Relocates once when the step being displayed has ceased to apply, moving to
   * the next applicable step in the declared order (or the last applicable step
   * when the removed step was at the end). Nothing is validated, skipped, or
   * submitted, and the call is a no-op while the current step still applies.
   * Returns true when the position changed.
   */
  reconcileApplicability(): boolean {
    const applicable = this.applicableSteps;
    if (applicable.some((step) => step.id === this.currentId)) return false;
    const removedIndex = this.steps.findIndex((step) => step.id === this.currentId);
    const next =
      applicable.find((step) => this.steps.indexOf(step) > removedIndex) ??
      applicable[applicable.length - 1];
    if (!next) return false;
    this.currentId = next.id;
    return true;
  }

  /** Live values view; mutations persist across navigation (retention). */
  get values(): WizardValues {
    return this.valueStore;
  }

  /** Zero-based index of the current step among the applicable steps. */
  get index(): number {
    const index = this.applicableSteps.findIndex((step) => step.id === this.currentId);
    return index === -1 ? 0 : index;
  }

  /** Total number of applicable input steps. */
  get total(): number {
    return this.applicableSteps.length;
  }

  /** The current step definition. */
  get current(): WizardStep {
    const applicable = this.applicableSteps;
    return applicable.find((step) => step.id === this.currentId) ?? applicable[0];
  }

  get isFirst(): boolean {
    return this.index === 0;
  }

  get isLast(): boolean {
    return this.index === this.total - 1;
  }

  /** True once the user has advanced past the final input step. */
  get isComplete(): boolean {
    return this.completed;
  }

  progress(): WizardProgress {
    const current = this.index + 1;
    return { current, total: this.total, ratio: current / this.total };
  }

  /** Validates the current step without navigating. */
  validateCurrent(): WizardFieldError[] {
    return this.current.validate(this.valueStore);
  }

  /**
   * Validates the current step and advances when valid. On the last step a
   * successful validation marks the wizard complete. Returns the validation
   * outcome so the caller can surface inline errors and move focus.
   */
  next(): WizardAdvanceResult {
    const errors = this.validateCurrent();
    if (errors.length > 0) return { ok: false, errors };

    this.advance();
    return { ok: true, errors: [] };
  }

  /** Moves to the next applicable step, or records completion on the last. */
  private advance(): void {
    if (this.isLast) {
      this.completed = true;
      return;
    }
    this.currentId = this.applicableSteps[this.index + 1].id;
  }

  /**
   * Advances past an optional step without validation. Values already entered
   * are preserved unless the step defines {@link WizardStep.onSkip}, which runs
   * first and may clear the answers the skip abandons. Throws if called on a
   * required step so the required/optional contract cannot be violated by
   * callers.
   */
  skip(): WizardAdvanceResult {
    if (!this.current.optional) {
      throw new Error(`Step "${this.current.id}" is required and cannot be skipped.`);
    }
    if (this.current.onSkip) this.current.onSkip(this.valueStore);
    this.advance();
    return { ok: true, errors: [] };
  }

  /**
   * Moves to the previous step, retaining all entered values. When the wizard
   * is complete, back returns to the final input step (clearing completion)
   * rather than stepping past it. No-op on step 1.
   */
  back(): void {
    if (this.completed) {
      this.completed = false;
      return;
    }
    if (this.isFirst) return;
    this.currentId = this.applicableSteps[this.index - 1].id;
  }
}
